/**
 * ====================================================================
 * FIKO CAD DRAGGABLE SUPPORT
 * ====================================================================
 *
 * Dimension sürükleme/taşıma sistemi.
 * CATIA/IronCAD tarzında profesyonel sürükleme.
 *
 * Performans Optimizasyonları:
 * - Zero-allocation inner loops
 * - Pre-allocated Vector3 buffers
 * - Throttled updates (60fps cap)
 * - RAF-based smooth dragging
 * - Event pooling
 *
 * @author FikoYT CAD Team
 * @version 1.0.0
 */

import * as THREE from 'three';
import { Dimension, DimensionState } from './DimensionTypes';
import type { DimensionManager } from './DimensionManager';

/**
 * Drag mode - Sürükleme modu
 * 🎯 v2.0.2: Offset sürükleme veya döndürme
 */
enum DragMode {
    OFFSET = 'OFFSET',    // Normal sürükleme (offset değişir)
    ROTATE = 'ROTATE'     // Döndürme (Shift tuşu ile)
}

/**
 * Drag state
 * 🎯 v2.0.0: CATIA tarzı sürükleme - offset değişir, orijinal noktalar sabit kalır
 * 🎯 v2.0.2: Döndürme desteği eklendi
 */
interface DragState {
    active: boolean;
    dimension: Dimension | null;
    startMouse: THREE.Vector2;
    startOffset: THREE.Vector3;      // Eski: position offset, Yeni: kullanılmıyor
    currentOffset: THREE.Vector3;    // Eski: position offset, Yeni: kullanılmıyor
    plane: THREE.Plane;
    // 🎯 v2.0.0: CATIA tarzı offset sürükleme
    startDimensionOffset: number;    // Başlangıç dimension offset değeri
    offsetDirection: THREE.Vector3;  // Offset artış/azalış yönü
    // 🎯 v2.0.2: Döndürme desteği
    mode: DragMode;                  // Sürükleme modu
    startRotation: number;           // Başlangıç döndürme açısı (radyan)
}

/**
 * Draggable Support - Sürükleme sistemi
 */
export class DraggableSupport {
    // Manager referansı
    private manager: DimensionManager;

    // DOM element
    private domElement: HTMLElement | null = null;

    // Drag state
    private dragState: DragState = {
        active: false,
        dimension: null,
        startMouse: new THREE.Vector2(),
        startOffset: new THREE.Vector3(),
        currentOffset: new THREE.Vector3(),
        plane: new THREE.Plane(),
        // 🎯 v2.0.0: CATIA tarzı offset sürükleme
        startDimensionOffset: 0,
        offsetDirection: new THREE.Vector3(),
        // 🎯 v2.0.2: Döndürme desteği
        mode: DragMode.OFFSET,
        startRotation: 0
    };

    // Hover state
    private hoveredDimension: Dimension | null = null;

    // 🚀 PERFORMANS: Pre-allocated buffers (zero-allocation)
    private static readonly _tempVec2 = new THREE.Vector2();
    private static readonly _tempVec3 = new THREE.Vector3();
    private static readonly _tempVec3_2 = new THREE.Vector3();
    private static readonly _tempVec3_3 = new THREE.Vector3();
    private static readonly _tempRay = new THREE.Ray();
    private static readonly _raycaster = new THREE.Raycaster();

    // 🚀 PERFORMANS: Throttle settings
    private lastMoveTime: number = 0;
    private readonly MOVE_THROTTLE_MS = 16; // ~60fps
    private rafId: number | null = null;
    private pendingMouse: THREE.Vector2 | null = null;
    // 🎯 v2.0.3: Bekleyen event için shiftKey durumu
    private pendingShiftKey: boolean = false;

    // Bound event handlers (GC optimization)
    private boundOnPointerDown: (e: PointerEvent) => void;
    private boundOnPointerMove: (e: PointerEvent) => void;
    private boundOnPointerUp: (e: PointerEvent) => void;
    private boundOnPointerCancel: (e: PointerEvent) => void;
    // 🎯 v2.0.12: Shift tuşu bırakıldığında mod değişikliği için
    private boundOnKeyUp: (e: KeyboardEvent) => void;
    private boundOnKeyDown: (e: KeyboardEvent) => void;
    // 🎯 v2.0.12: Son bilinen mouse pozisyonu (keyup event'i için)
    private lastMousePosition: THREE.Vector2 = new THREE.Vector2();

    constructor(manager: DimensionManager) {
        this.manager = manager;

        // 🚀 Bound handlers (bind once, reuse)
        this.boundOnPointerDown = this.onPointerDown.bind(this);
        this.boundOnPointerMove = this.onPointerMove.bind(this);
        this.boundOnPointerUp = this.onPointerUp.bind(this);
        this.boundOnPointerCancel = this.onPointerUp.bind(this);
        // 🎯 v2.0.12: Keyboard handlers
        this.boundOnKeyUp = this.onKeyUp.bind(this);
        this.boundOnKeyDown = this.onKeyDown.bind(this);
    }

    /**
     * Event listener'ları bağla
     */
    attach(domElement: HTMLElement): void {
        if (this.domElement) {
            this.detach();
        }

        this.domElement = domElement;

        // 🎯 FIX: Tüm listener'lar passive: false olmalı (stopPropagation için)
        // Capture phase kullanarak diğer handler'lardan önce yakala
        domElement.addEventListener('pointerdown', this.boundOnPointerDown, { passive: false, capture: true });
        domElement.addEventListener('pointermove', this.boundOnPointerMove, { passive: false, capture: true });
        domElement.addEventListener('pointerup', this.boundOnPointerUp, { passive: false, capture: true });
        domElement.addEventListener('pointercancel', this.boundOnPointerCancel, { passive: false, capture: true });
        domElement.addEventListener('pointerleave', this.boundOnPointerUp, { passive: false, capture: true });

        // 🎯 v2.0.12: Keyboard event'leri window'a ekle (Shift tuşu için)
        window.addEventListener('keyup', this.boundOnKeyUp);
        window.addEventListener('keydown', this.boundOnKeyDown);
    }

    /**
     * Event listener'ları kaldır
     */
    detach(): void {
        if (!this.domElement) return;

        // 🎯 FIX: capture: true ile eklendiyse, kaldırırken de capture: true olmalı
        this.domElement.removeEventListener('pointerdown', this.boundOnPointerDown, { capture: true });
        this.domElement.removeEventListener('pointermove', this.boundOnPointerMove, { capture: true });
        this.domElement.removeEventListener('pointerup', this.boundOnPointerUp, { capture: true });
        this.domElement.removeEventListener('pointercancel', this.boundOnPointerCancel, { capture: true });
        this.domElement.removeEventListener('pointerleave', this.boundOnPointerUp, { capture: true });

        // 🎯 v2.0.12: Keyboard event'lerini kaldır
        window.removeEventListener('keyup', this.boundOnKeyUp);
        window.removeEventListener('keydown', this.boundOnKeyDown);

        this.domElement = null;

        // RAF temizle
        if (this.rafId !== null) {
            cancelAnimationFrame(this.rafId);
            this.rafId = null;
        }
    }

    /**
     * Pointer down handler
     */
    private onPointerDown(event: PointerEvent): void {
        if (event.button !== 0) return; // Sadece sol tık

        const scene = this.manager.getScene();
        if (!scene?.anaGosterim?.aktifKamera) return;

        // Mouse pozisyonunu normalize et
        const mouse = this.getNormalizedMouse(event);

        // Hit test
        const dimension = this.hitTest(mouse);

        if (dimension && dimension.options.draggable !== false) {
            // 🎯 FIX: Event'i tamamen durdur - FikoSecililer'e ulaşmasın
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();

            // 🎯 v2.0.2: Shift tuşu ile döndürme modu
            const mode = event.shiftKey ? DragMode.ROTATE : DragMode.OFFSET;

            // Drag başlat
            this.startDrag(dimension, mouse, mode);
        }
    }

    /**
     * Pointer move handler
     */
    private onPointerMove(event: PointerEvent): void {
        // 🎯 FIX: Sürükleme aktifken event'i durdur
        if (this.dragState.active) {
            event.preventDefault();
            event.stopPropagation();
        }

        const now = performance.now();

        // 🚀 THROTTLE: 60fps cap
        if (now - this.lastMoveTime < this.MOVE_THROTTLE_MS) {
            // Pending mouse kaydet (RAF'ta işlenecek)
            if (!this.pendingMouse) {
                this.pendingMouse = new THREE.Vector2();
            }
            this.pendingMouse.set(event.clientX, event.clientY);
            // 🎯 v2.0.3: ShiftKey durumunu da kaydet
            this.pendingShiftKey = event.shiftKey;

            // RAF schedule et
            if (this.rafId === null) {
                this.rafId = requestAnimationFrame(() => {
                    this.rafId = null;
                    if (this.pendingMouse) {
                        this.processPendingMove();
                    }
                });
            }
            return;
        }

        this.lastMoveTime = now;
        this.processMove(event);
    }

    /**
     * Bekleyen move'u işle
     * 🎯 v2.0.3: pendingShiftKey de kullanılıyor
     */
    private processPendingMove(): void {
        if (!this.pendingMouse || !this.domElement) return;

        // Fake event oluştur
        const rect = this.domElement.getBoundingClientRect();
        const mouse = DraggableSupport._tempVec2.set(
            ((this.pendingMouse.x - rect.left) / rect.width) * 2 - 1,
            -((this.pendingMouse.y - rect.top) / rect.height) * 2 + 1
        );

        // 🎯 v2.0.3: ShiftKey durumunu al ve sıfırla
        const shiftKey = this.pendingShiftKey;
        this.pendingMouse = null;
        this.pendingShiftKey = false;

        if (this.dragState.active) {
            this.updateDrag(mouse, shiftKey);
        } else {
            this.updateHover(mouse);
        }
    }

    /**
     * Move işle
     * 🎯 v2.0.3: ShiftKey'i updateDrag'e ilet
     * 🎯 v2.0.12: lastMousePosition'ı güncelle (keyup event için)
     */
    private processMove(event: PointerEvent): void {
        const mouse = this.getNormalizedMouse(event);

        // 🎯 v2.0.12: Son mouse pozisyonunu kaydet (Shift keyup event'i için)
        this.lastMousePosition.copy(mouse);

        if (this.dragState.active) {
            this.updateDrag(mouse, event.shiftKey);
        } else {
            this.updateHover(mouse);
        }
    }

    /**
     * Pointer up handler
     */
    private onPointerUp(event: PointerEvent): void {
        if (this.dragState.active) {
            // 🎯 FIX: Sürükleme aktifken event'i durdur
            // Bu sayede FikoSecililer'in tiklandi event'i tetiklenmez
            event.preventDefault();
            event.stopPropagation();
            event.stopImmediatePropagation();

            this.endDrag();
        }
    }

    /**
     * 🎯 v2.0.12: Keyboard up handler - Shift bırakıldığında mod değiştir
     */
    private onKeyUp(event: KeyboardEvent): void {
        // Sadece Shift tuşu ve aktif sürükleme varsa
        if (event.key === 'Shift' && this.dragState.active) {
            // ROTATE modundan OFFSET moduna geç
            // updateDrag çağır, shiftKey=false ile
            this.updateDrag(this.lastMousePosition, false);
        }
    }

    /**
     * 🎯 v2.0.12: Keyboard down handler - Shift basıldığında mod değiştir
     */
    private onKeyDown(event: KeyboardEvent): void {
        // Sadece Shift tuşu ve aktif sürükleme varsa
        if (event.key === 'Shift' && this.dragState.active) {
            // OFFSET modundan ROTATE moduna geç
            // updateDrag çağır, shiftKey=true ile
            this.updateDrag(this.lastMousePosition, true);
        }
    }

    /**
     * Drag başlat
     * 🎯 v2.0.0: CATIA tarzı - offset değişir, orijinal noktalar sabit kalır
     * 🎯 v2.0.2: Döndürme modu desteği
     */
    private startDrag(dimension: Dimension, mouse: THREE.Vector2, mode: DragMode = DragMode.OFFSET): void {
        const scene = this.manager.getScene();
        if (!scene?.anaGosterim?.aktifKamera) return;

        const camera = scene.anaGosterim.aktifKamera;

        // State güncelle
        dimension.state = DimensionState.DRAGGING;

        // Drag plane oluştur (kameraya paralel)
        const cameraDirection = DraggableSupport._tempVec3.set(0, 0, -1);
        cameraDirection.applyQuaternion(camera.quaternion);

        const dimensionPos = DraggableSupport._tempVec3_2;
        dimension.getWorldPosition(dimensionPos);

        this.dragState.plane.setFromNormalAndCoplanarPoint(cameraDirection, dimensionPos);

        // Başlangıç değerlerini kaydet
        this.dragState.active = true;
        this.dragState.dimension = dimension;
        this.dragState.startMouse.copy(mouse);

        // 🎯 v2.0.2: Sürükleme modunu kaydet
        this.dragState.mode = mode;

        // 🎯 v2.0.0: CATIA tarzı offset sürükleme
        // Mevcut offset değerini kaydet
        this.dragState.startDimensionOffset = dimension.getOffset();

        // 🎯 v2.0.2: Mevcut döndürme açısını kaydet
        this.dragState.startRotation = dimension.getRotation();

        // 🎯 v2.0.1 FIX: Offset yönünü dimension'dan al (kamera bağımsız!)
        // Kamera döndüğünde bile offset yönü sabit kalır
        // Dimension'ın geometrisine göre hesaplanır (pointA → projectedA yönü)
        this.dragState.offsetDirection.copy(dimension.getOffsetDirection());

        // 🎯 v2.0.2: Cursor moduna göre değiştir
        if (this.domElement) {
            this.domElement.style.cursor = 'grabbing';
        }
    }

    /**
     * Drag güncelle
     * 🎯 v2.0.0: CATIA tarzı - offset değişir, dimension pozisyonu sabit
     * 🎯 v2.0.2: Döndürme modu desteği
     * 🎯 v2.0.3: Gerçek zamanlı Shift takibi - sürüklerken Shift basınca döndür, bırakınca sürükle
     * 🎯 v2.0.4 FIX: Mod değiştiğinde kesintisiz geçiş - mouse pozisyonundan devam et
     */
    private updateDrag(mouse: THREE.Vector2, shiftKey: boolean = false): void {
        if (!this.dragState.active || !this.dragState.dimension) return;

        const scene = this.manager.getScene();
        if (!scene?.anaGosterim?.aktifKamera) return;

        const camera = scene.anaGosterim.aktifKamera;

        // 🎯 v2.0.3: Gerçek zamanlı mod değişikliği
        // Shift basılıyken döndürme, değilken offset
        const currentMode = shiftKey ? DragMode.ROTATE : DragMode.OFFSET;

        // 🎯 v2.0.4 FIX: Mod değiştiyse mevcut mouse pozisyonunu yeni başlangıç yap
        // Bu sayede sıçrama olmaz - mouse tam olduğu yerden devam eder
        if (currentMode !== this.dragState.mode) {
            this.dragState.mode = currentMode;
            // Yeni mod için başlangıç noktasını MEVCUT MOUSE POZİSYONUNA sıfırla
            this.dragState.startMouse.copy(mouse);
            // Mevcut dimension değerlerini yeni başlangıç olarak kaydet
            this.dragState.startDimensionOffset = this.dragState.dimension.getOffset();
            this.dragState.startRotation = this.dragState.dimension.getRotation();

            // 🎯 v2.0.11: ROTATE → OFFSET moduna geçişte offset yönünü yeniden hesapla
            // Döndürme sonrası yön değişmiş olabilir (örn. 180° döndürme)
            if (currentMode === DragMode.OFFSET) {
                this.dragState.offsetDirection.copy(this.dragState.dimension.getOffsetDirection());
            }
            // 🎯 İlk frame'de delta = 0 olacak, bu yüzden return yok
            // Bir sonraki frame'de hareket başlayacak
        }

        // Ray oluştur
        DraggableSupport._raycaster.setFromCamera(mouse, camera);

        // Plane ile kesişim bul
        const intersection = DraggableSupport._tempVec3;
        const ray = DraggableSupport._raycaster.ray;

        if (ray.intersectPlane(this.dragState.plane, intersection)) {
            // Başlangıç kesişimi
            DraggableSupport._raycaster.setFromCamera(this.dragState.startMouse, camera);
            const startIntersection = DraggableSupport._tempVec3_2;

            if (DraggableSupport._raycaster.ray.intersectPlane(this.dragState.plane, startIntersection)) {
                // Delta hesapla (3D hareket)
                const delta = intersection.clone().sub(startIntersection);

                // 🎯 v2.0.3: Mevcut moda göre işlem yap
                if (this.dragState.mode === DragMode.ROTATE) {
                    // 🎯 CATIA TARZI DÖNDÜRME: Fareyi pivot (ölçüm orta noktası) etrafında
                    // AÇISAL takip et — dimension imlece doğru döner. Eski lineer mouseX*π*3
                    // mantığı aşırı hassastı ve fareye göre dönmüyordu.
                    const dim: any = this.dragState.dimension;
                    const pivot = DraggableSupport._tempVec3_3;
                    const op = dim.getOriginalPoints?.();
                    // 🎯 KRİTİK: setRotation, offset yönünü ÖLÇÜM EKSENİ (A→B) etrafında döndürür.
                    // Bu yüzden fare açısını da AYNI eksen etrafında ölçmeliyiz. Önceden kamera ekseni
                    // (plane.normal) kullanılıyordu → eksenler uyuşmuyor, döndürme çalışmıyordu.
                    let n: THREE.Vector3;
                    if (op?.pointA && op?.pointB) {
                        pivot.copy(op.pointA).add(op.pointB).multiplyScalar(0.5);
                        n = op.pointB.clone().sub(op.pointA).normalize(); // ölçüm ekseni = döndürme ekseni
                    } else {
                        pivot.copy(dim.position || intersection);
                        n = this.dragState.plane.normal;
                    }
                    // Fare vektörlerini eksene DİK düzleme yansıt → eksen etrafındaki gerçek açı
                    const v0 = startIntersection.clone().sub(pivot).projectOnPlane(n);
                    const v1 = intersection.clone().sub(pivot).projectOnPlane(n);
                    if (v0.lengthSq() > 1e-8 && v1.lengthSq() > 1e-8) {
                        const angleDelta = Math.atan2(v0.clone().cross(v1).dot(n), v0.dot(v1));
                        this.dragState.dimension.setRotation(this.dragState.startRotation + angleDelta);
                    }
                } else {
                    // OFFSET MODU (varsayılan)
                    // 🎯 v2.0.0: CATIA tarzı offset sürükleme
                    // Delta vektörünü offset yönüne projekte et
                    const offsetDelta = delta.dot(this.dragState.offsetDirection);

                    // 🎯 v2.0.5 FIX: Negatif offset da destekleniyor - her iki yöne sürüklenebilir
                    // Eski: Math.max(5, ...) - sadece pozitif yöne izin veriyordu
                    // Yeni: Sınırsız - CATIA/SolidWorks gibi her iki yöne sürüklenebilir
                    const newOffset = this.dragState.startDimensionOffset + offsetDelta;

                    // Dimension'ın offset'ini güncelle (geometri yeniden oluşturulur)
                    this.dragState.dimension.setOffset(newOffset);
                }
            }
        }
    }

    /**
     * Drag bitir
     */
    private endDrag(): void {
        if (this.dragState.dimension) {
            this.dragState.dimension.state = DimensionState.NORMAL;

            // Event emit
            this.manager['emit']?.('dimensionDragged', {
                id: this.dragState.dimension.userData.dimensionId,
                position: this.dragState.dimension.position.clone()
            });
        }

        // State temizle
        this.dragState.active = false;
        this.dragState.dimension = null;

        // Cursor normal
        if (this.domElement) {
            this.domElement.style.cursor = '';
        }
    }

    /**
     * Hover güncelle
     */
    private updateHover(mouse: THREE.Vector2): void {
        const dimension = this.hitTest(mouse);

        // Önceki hover'ı kaldır
        if (this.hoveredDimension && this.hoveredDimension !== dimension) {
            if (this.hoveredDimension.state === DimensionState.HOVERED) {
                this.hoveredDimension.state = DimensionState.NORMAL;
            }
        }

        // Yeni hover uygula
        if (dimension && dimension !== this.hoveredDimension) {
            if (dimension.options.selectable !== false) {
                dimension.state = DimensionState.HOVERED;

                if (this.domElement) {
                    this.domElement.style.cursor = dimension.options.draggable !== false ? 'grab' : 'pointer';
                }
            }
        } else if (!dimension && this.domElement) {
            this.domElement.style.cursor = '';
        }

        this.hoveredDimension = dimension;
    }

    /**
     * Hit test - Mouse altındaki dimension'ı bul
     */
    private hitTest(mouse: THREE.Vector2): Dimension | null {
        const scene = this.manager.getScene();
        if (!scene?.anaGosterim?.aktifKamera) return null;

        const camera = scene.anaGosterim.aktifKamera;
        const container = this.manager.getContainer();

        DraggableSupport._raycaster.setFromCamera(mouse, camera);

        // Dimension container'ını tara
        const intersects = DraggableSupport._raycaster.intersectObjects(container.children, true);

        for (const intersect of intersects) {
            // Dimension'ı bul (parent'a çık)
            let obj: THREE.Object3D | null = intersect.object;
            while (obj) {
                if (obj.userData.dimensionId) {
                    return obj as Dimension;
                }
                obj = obj.parent;
            }
        }

        return null;
    }

    /**
     * Normalized mouse pozisyonu al
     */
    private getNormalizedMouse(event: PointerEvent): THREE.Vector2 {
        if (!this.domElement) {
            return DraggableSupport._tempVec2.set(0, 0);
        }

        const rect = this.domElement.getBoundingClientRect();
        return DraggableSupport._tempVec2.set(
            ((event.clientX - rect.left) / rect.width) * 2 - 1,
            -((event.clientY - rect.top) / rect.height) * 2 + 1
        );
    }

    /**
     * 🎯 Sürükleme aktif mi kontrol et
     */
    isDragging(): boolean {
        return this.dragState.active;
    }

    /**
     * 🎯 Hover aktif mi kontrol et
     */
    isHovering(): boolean {
        return this.hoveredDimension !== null;
    }

    /**
     * Temizlik
     */
    dispose(): void {
        this.detach();
        this.dragState.dimension = null;
        this.hoveredDimension = null;
    }
}
